import { lazy, Suspense, useCallback, useState } from "react";
import { Button, Dropdown } from "antd";
import type { MenuProps } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useExportPdf } from "./useExportPdf";
import { SendDashboardEmailModal } from "./SendDashboardEmailModal";
import { useDashboard } from "../Contexts/dashboardHooks";
import { trainingAPI } from "@api/TrainingAPI";
import { audioMessage } from "@components/Common/Notifications/audioMessage";

const PdfAnnotatorModal = lazy(() =>
  import("@components/PDFAnnotator/PdfAnnotatorModal").then((m) => ({ default: m.PdfAnnotatorModal })),
);

const PDF_ATTACHMENT_NAME = "SandboxDashboard.pdf";

/**
 * The toolbar's "Export PDF" control: export-only, annotate-then-export or send by
 * email, plus the lazy-loaded annotator modal. Every path downloads the PDF the
 * server renders (see useExportPdf), so it matches the scheduled report.
 */
export function PdfExportControl() {
  const { exportPdf, generateBlob, exporting } = useExportPdf();
  const { state } = useDashboard();
  const [annotatorFile, setAnnotatorFile] = useState<{ buffer: ArrayBuffer; name: string } | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleAnnotatorExport = useCallback(async (buffer: ArrayBuffer, name: string) => {
    try {
      const file = new File([buffer], name, { type: "application/pdf" });
      await trainingAPI.storeAnnotatedPdfS3(file);
    } catch (err) {
      console.error(err);
      audioMessage.error("Could not save the annotated PDF.");
    }
  }, []);

  const downloadAndAnnotate = async () => {
    const blob = await generateBlob();
    if (!blob) {
      audioMessage.error("Could not generate the PDF.");
      return;
    }
    setAnnotatorFile({ buffer: await blob.arrayBuffer(), name: "dashboard.pdf" });
  };

  // Fetches the server-rendered PDF and hands it to the generic send-email endpoint.
  // The board is identified by the subject/message the user composed, so the
  // attachment name is generic and no dashboard id is sent.
  const sendEmail = async ({
    emails,
    subject,
    message,
  }: {
    emails: string[];
    subject: string;
    message: string;
  }) => {
    setSendingEmail(true);
    try {
      const blob = await generateBlob();
      if (!blob) {
        audioMessage.error("Could not generate the PDF.");
        return;
      }
      const file = new File([blob], PDF_ATTACHMENT_NAME, { type: "application/pdf" });
      await trainingAPI.sendEmail({ emails, subject, message, file });
      audioMessage.success("Dashboard has been sent.");
      setEmailModalOpen(false);
    } catch (err) {
      console.error("Dashboard email failed:", err);
      audioMessage.error("Failed to send the dashboard email.");
    } finally {
      setSendingEmail(false);
    }
  };

  const items: MenuProps["items"] = [
    { key: "download", label: "Export Only" },
    { key: "annotate", label: "Annotate + Export" },
    { key: "email", label: "Send Email" },
  ];

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "download") exportPdf();
    else if (key === "annotate") downloadAndAnnotate();
    // Mounting the modal fresh resets any previous recipient selection.
    else if (key === "email") setEmailModalOpen(true);
  };

  return (
    <>
      <Dropdown menu={{ items, onClick: onMenuClick }} placement="bottomRight">
        <Button loading={exporting}>
          Export PDF <DownOutlined />
        </Button>
      </Dropdown>
      {emailModalOpen && (
        <SendDashboardEmailModal
          dashboardName={state.name}
          sending={sendingEmail}
          busy={exporting}
          onCancel={() => setEmailModalOpen(false)}
          onSend={sendEmail}
        />
      )}
      {annotatorFile && (
        <Suspense fallback={null}>
          <PdfAnnotatorModal
            open
            initialFile={annotatorFile}
            onClose={() => setAnnotatorFile(null)}
            onExportReady={handleAnnotatorExport}
          />
        </Suspense>
      )}
    </>
  );
}
